import { getCurrentScale } from './get-scale.js';
import { isInPixelRange } from './is-in-pixel-range.js';
import { getAccessors, getHitBoxForDatapoint } from './query-hovered.js';
import { isBarSeries, isRectSeries } from './type-guards.js';
import { relAccessor } from './value-representation.js';
import type { Point } from '../../core/types/point.js';
import { safeGet } from '../../core/utils/safe-get.js';
import type {
  BarSeriesQueryResult,
  HoverGrid,
  HoverGridDatapoint,
  HoverGridEntry,
  RectSeriesQueryResult,
  SeriesQueryResult,
  SeriesToHoverGrid,
} from '../context/XYChartOverlayPerformance.context.js';
import type { AxisIdToScales } from '../context/XYChartScales.context.js';
import {
  type AxisIdToScaleDomain,
  type BarSeriesWithDatapoints,
  type MapDatapoints,
  type RectSeriesWithDatapoints,
  type SeriesToMetadata,
} from '../types/xy-chart-internals.js';

// Picked as a reasonable default for typical chart densities;
// dynamic cell-size adaptation is intentionally deferred.
const HOVER_GRID_CELL_SIZE = 32;

function buildSingleHoverGrid(
  series: RectSeriesWithDatapoints | BarSeriesWithDatapoints,
  axisIdToScales: AxisIdToScales,
  datapointMetadataMap: MapDatapoints,
  seriesToMetadata: SeriesToMetadata,
  axisIdToScaleDomain: AxisIdToScaleDomain,
): HoverGrid<HoverGridDatapoint> {
  const { xAxis, yAxis } = safeGet(seriesToMetadata, series);
  const xRelAccessor = relAccessor(xAxis, axisIdToScaleDomain);
  const yRelAccessor = relAccessor(yAxis, axisIdToScaleDomain);

  const accessors = getAccessors(
    series,
    axisIdToScales,
    datapointMetadataMap,
    xRelAccessor,
    yRelAccessor,
  );

  const xScale = getCurrentScale(axisIdToScales, series.xAxisId, series.type);
  const yScale = getCurrentScale(axisIdToScales, series.yAxisId, series.type);
  const [xRangeA, xRangeB] = xScale.range();
  const [yRangeA, yRangeB] = yScale.range();
  const originX = Math.min(xRangeA, xRangeB);
  const originY = Math.min(yRangeA, yRangeB);
  const maxX = Math.max(xRangeA, xRangeB);
  const maxY = Math.max(yRangeA, yRangeB);

  const cols = Math.max(1, Math.ceil((maxX - originX) / HOVER_GRID_CELL_SIZE));
  const rows = Math.max(1, Math.ceil((maxY - originY) / HOVER_GRID_CELL_SIZE));
  const cells: HoverGridEntry<HoverGridDatapoint>[][] = Array.from(
    { length: cols * rows },
    () => [],
  );

  const data = isRectSeries(series) ? series.filteredData : series.data;
  const isBar = isBarSeries(series);

  for (const datapoint of data) {
    const metadata = safeGet(datapointMetadataMap, datapoint);
    if (metadata.isGap) {
      continue;
    }

    const {
      x: hitX,
      y: hitY,
      width: hitWidth,
      height: hitHeight,
    } = getHitBoxForDatapoint(
      datapoint,
      accessors,
      isBar,
      xScale,
      yScale,
      metadata.baseline,
    );

    const colStart = Math.max(
      0,
      Math.floor((hitX - originX) / HOVER_GRID_CELL_SIZE),
    );
    const colEnd = Math.min(
      cols - 1,
      Math.floor((hitX + hitWidth - originX) / HOVER_GRID_CELL_SIZE),
    );
    const rowStart = Math.max(
      0,
      Math.floor((hitY - originY) / HOVER_GRID_CELL_SIZE),
    );
    const rowEnd = Math.min(
      rows - 1,
      Math.floor((hitY + hitHeight - originY) / HOVER_GRID_CELL_SIZE),
    );

    if (colStart > colEnd || rowStart > rowEnd) {
      continue;
    }

    const entry: HoverGridEntry<HoverGridDatapoint> = {
      datapoint,
      x: hitX,
      y: hitY,
      width: hitWidth,
      height: hitHeight,
    };

    for (let row = rowStart; row <= rowEnd; row++) {
      for (let col = colStart; col <= colEnd; col++) {
        cells[row * cols + col].push(entry);
      }
    }
  }

  return {
    cells,
    cellSize: HOVER_GRID_CELL_SIZE,
    originX,
    originY,
    cols,
    rows,
  };
}

/**
 * Build per-series hover grids for fast point-in-rect queries.
 *
 * Each cell holds entries for datapoints whose pixel bounding box
 * intersects that cell, enabling near-constant-time lookup at hover
 * time regardless of total datapoint count.
 */
export function buildHoverGrids(
  seriesWithDatapoints: (RectSeriesWithDatapoints | BarSeriesWithDatapoints)[],
  axisIdToScales: AxisIdToScales,
  datapointMetadataMap: MapDatapoints,
  seriesToMetadata: SeriesToMetadata,
  axisIdToScaleDomain: AxisIdToScaleDomain,
): SeriesToHoverGrid {
  const seriesToGrids: SeriesToHoverGrid = new Map();

  for (const series of seriesWithDatapoints) {
    const grid = buildSingleHoverGrid(
      series,
      axisIdToScales,
      datapointMetadataMap,
      seriesToMetadata,
      axisIdToScaleDomain,
    );
    seriesToGrids.set(series.id, grid);
  }

  return seriesToGrids;
}

function findInGrid(
  grid: HoverGrid<HoverGridDatapoint>,
  position: Point,
): HoverGridDatapoint | undefined {
  const { cells, cellSize, originX, originY, cols, rows } = grid;

  const col = Math.floor((position.x - originX) / cellSize);
  const row = Math.floor((position.y - originY) / cellSize);

  if (col < 0 || col >= cols || row < 0 || row >= rows) {
    return undefined;
  }

  const candidates = cells[row * cols + col];
  for (const entry of candidates) {
    if (
      isInPixelRange(position.x, entry.x, entry.width) &&
      isInPixelRange(position.y, entry.y, entry.height)
    ) {
      return entry.datapoint;
    }
  }
  return undefined;
}

/**
 * Build a query that returns every series whose hovered datapoint
 * sits under the cursor, backed by the same per-series grids used by
 * the single-tooltip path. Mirrors `buildQueryAllHovered` but trades
 * the O(series × datapoints) linear scan for an O(series) cell
 * lookup — the hot path the shared tooltip walks on every mouse
 * move.
 */
export function buildQueryAllHoveredFromGrid(
  seriesIdToGrid: SeriesToHoverGrid,
) {
  return (
    arrayOfSeries: (RectSeriesWithDatapoints | BarSeriesWithDatapoints)[],
    position: Point,
  ): SeriesQueryResult[] => {
    if (position.x < 0 || position.y < 0) {
      return [];
    }

    const results: SeriesQueryResult[] = [];
    for (const series of arrayOfSeries) {
      const grid = seriesIdToGrid.get(series.id);
      if (!grid) {
        continue;
      }

      const datapoint = findInGrid(grid, position);
      if (!datapoint) {
        continue;
      }

      results.push({
        type: series.type,
        distance: 0,
        datapoint,
        series,
      } as SeriesQueryResult);
    }
    return results;
  };
}

/**
 * Build a hovered-datapoint query backed by pre-computed per-series
 * grids. Iterates the input series in reverse so the topmost
 * (last-rendered) overlapping datapoint wins, matching the legacy
 * `buildQueryHovered` behaviour.
 */
export function buildQueryHoveredFromGrid(seriesIdToGrid: SeriesToHoverGrid) {
  function query(
    arrayOfSeries: RectSeriesWithDatapoints[],
    position: Point,
  ): RectSeriesQueryResult | undefined;
  function query(
    arrayOfSeries: BarSeriesWithDatapoints[],
    position: Point,
  ): BarSeriesQueryResult | undefined;
  function query(
    arrayOfSeries: (RectSeriesWithDatapoints | BarSeriesWithDatapoints)[],
    position: Point,
  ) {
    if (position.x < 0 || position.y < 0) {
      return undefined;
    }

    for (let i = arrayOfSeries.length - 1; i >= 0; i--) {
      const series = arrayOfSeries[i];
      const grid = seriesIdToGrid.get(series.id);
      if (!grid) {
        continue;
      }

      const datapoint = findInGrid(grid, position);
      if (!datapoint) {
        continue;
      }

      return {
        type: series.type,
        distance: 0,
        datapoint,
        series,
      };
    }
    return undefined;
  }
  return query;
}
